/**
 * Boutons d’édition visibles selon le rôle et l’ancrage.
 * Super-admin et développeur : tout. Admin : sa direction et l’arbre en dessous, sans créer
 * ni supprimer une direction. Manageur : enfants de son ancrage, pas le nœud d’ancrage
 * ni les entités sœurs hors de ce nœud.
 */
import { isDeveloper, isLegacyAdmin, isManager, isSuperAdmin } from './abilities'

type OrgLeaderScope = {
  directionLeaders: Array<{ dirLib: string }>
  domainLeaders: Array<{ dirLib: string; domLib: string }>
  agenceLeaders: Array<{ dirLib: string; domLib: string; ageLib: string }>
}

type OrgPositionRef = {
  dirLib: string
  domLib: string
  ageLib: string
  equLib: string
}

export type OrgEditActor = {
  roles: string[]
  directionAdminDirLibs: string[]
  managerScope: OrgLeaderScope | null
  positions: OrgPositionRef[]
}

function sameOrg(left: string | undefined, right: string | undefined): boolean {
  return (left ?? '').trim().toLowerCase() === (right ?? '').trim().toLowerCase()
}

function isUnbounded(actor: OrgEditActor): boolean {
  return isSuperAdmin(actor.roles) || isDeveloper(actor.roles)
}

/** Manageur dont le périmètre n’est pas encore chargé : ne rien proposer plutôt qu’un bouton refusé. */
function awaitingManagerScope(actor: OrgEditActor): boolean {
  if (isUnbounded(actor)) {
    return false
  }
  if (!isManager(actor.roles)) {
    return false
  }
  return actor.managerScope === null
}

function adminCoversDirection(actor: OrgEditActor, dirLib: string): boolean {
  if (isUnbounded(actor)) {
    return true
  }
  if (!isLegacyAdmin(actor.roles)) {
    return false
  }
  return actor.directionAdminDirLibs.some((entry) => sameOrg(entry, dirLib))
}

function isDirectionLeader(actor: OrgEditActor, dirLib: string): boolean {
  return (actor.managerScope?.directionLeaders ?? []).some((entry) => sameOrg(entry.dirLib, dirLib))
}

function isDomainLeader(actor: OrgEditActor, dirLib: string, domLib: string): boolean {
  return (actor.managerScope?.domainLeaders ?? []).some(
    (entry) => sameOrg(entry.dirLib, dirLib) && sameOrg(entry.domLib, domLib),
  )
}

function isAgenceLeader(actor: OrgEditActor, dirLib: string, domLib: string, ageLib: string): boolean {
  return (actor.managerScope?.agenceLeaders ?? []).some(
    (entry) =>
      sameOrg(entry.dirLib, dirLib) &&
      sameOrg(entry.domLib, domLib) &&
      sameOrg(entry.ageLib, ageLib),
  )
}

function isMemberOfEquipe(
  actor: OrgEditActor,
  dirLib: string,
  domLib: string,
  ageLib: string,
  equLib: string,
): boolean {
  return actor.positions.some(
    (position) =>
      sameOrg(position.dirLib, dirLib) &&
      sameOrg(position.domLib, domLib) &&
      sameOrg(position.ageLib, ageLib) &&
      sameOrg(position.equLib, equLib),
  )
}

/** Création ou suppression d’une direction : super-admin et développeur. */
export function actorMayCreateOrDeleteDirection(actor: OrgEditActor): boolean {
  return isUnbounded(actor)
}

/** Réordonner les domaines d’une direction : même périmètre que l’ajout d’un domaine. */
export function actorMayReorderDomaines(actor: OrgEditActor, dirLib: string): boolean {
  return actorMayAddDomaine(actor, dirLib)
}

/** Carte direction de la vue nationale. Le manageur ne modifie pas le nœud direction. */
export function actorMayModifyDirection(actor: OrgEditActor, dirLib: string): boolean {
  if (awaitingManagerScope(actor)) {
    return false
  }
  if (isUnbounded(actor)) {
    return true
  }
  return adminCoversDirection(actor, dirLib)
}

/** CODIR de la vue nationale (équipe de type) : hors périmètre admin de direction. */
export function actorMayMutateNationalCodir(actor: OrgEditActor): boolean {
  return isUnbounded(actor)
}

/** Ajouter un domaine, ou le CODIR de la direction : ancrage direction, ou admin de cette direction. */
export function actorMayAddDomaine(actor: OrgEditActor, dirLib: string): boolean {
  if (awaitingManagerScope(actor)) {
    return false
  }
  if (adminCoversDirection(actor, dirLib)) {
    return true
  }
  return isDirectionLeader(actor, dirLib)
}

/** Modifier le domaine lui-même (pas seulement ses enfants). */
export function actorMayModifyDomaine(actor: OrgEditActor, dirLib: string, _domLib: string): boolean {
  return actorMayAddDomaine(actor, dirLib)
}

/**
 * Enfants d’un domaine (agence, équipe, CODIR de domaine) :
 * ancrage direction, ancrage de ce domaine, ou admin de la direction.
 */
export function actorMayMutateDomaineChildren(
  actor: OrgEditActor,
  dirLib: string,
  domLib: string,
): boolean {
  if (awaitingManagerScope(actor)) {
    return false
  }
  if (adminCoversDirection(actor, dirLib) || isDirectionLeader(actor, dirLib)) {
    return true
  }
  return isDomainLeader(actor, dirLib, domLib)
}

/** Supprimer une agence : ancrage direction ou domaine, pas l’ancrage agence. */
export function actorMayDeleteAgence(actor: OrgEditActor, dirLib: string, domLib: string): boolean {
  return actorMayMutateDomaineChildren(actor, dirLib, domLib)
}

/** Créer ou modifier l’entité équipe (pas seulement ses sections). */
export function actorMayModifyEquipe(
  actor: OrgEditActor,
  dirLib: string,
  domLib: string,
  ageLib: string,
): boolean {
  if (awaitingManagerScope(actor)) {
    return false
  }
  if (adminCoversDirection(actor, dirLib) || isDirectionLeader(actor, dirLib)) {
    return true
  }
  if (isDomainLeader(actor, dirLib, domLib)) {
    return true
  }
  return isAgenceLeader(actor, dirLib, domLib, ageLib)
}

/** Sections et agents de cette équipe, y compris l’ancrage équipe. */
export function actorMayMutateSections(
  actor: OrgEditActor,
  dirLib: string,
  domLib: string,
  ageLib: string,
  equLib: string,
): boolean {
  if (actorMayModifyEquipe(actor, dirLib, domLib, ageLib)) {
    return true
  }
  if (awaitingManagerScope(actor)) {
    return false
  }
  return isMemberOfEquipe(actor, dirLib, domLib, ageLib, equLib)
}
